import * as React from 'react';
import SearchField from '../components/SearchField';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useActivationDevices from '@/src/Activations/components/hooks/useActivationDevices';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useZipPhotos from '@/src/Activations/components/hooks/useZipPhotos';
import useShare from '@/src/Share/components/hooks/useShare';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import useMediaTags, {
    MediaTag,
} from '@/src/Activations/components/hooks/useMediaTags';
import BulkUploadMedia from '@/src/Common/components/BulkUploadMedia';
import {
    BulkEditModal,
    EditBatchProgress,
    EditHistoryModal,
    VersionHistoryModal,
} from '@/src/Common/components/MediaBulkEdit';
import { EditBatch } from '@/src/Common/components/hooks/useMediaEdit';
import { useEventContext } from '../components/EventContext';
import { eventDay } from '../lib/eventStats';
import { toast } from 'sonner';
import { useDialogRef, dialogPanelProps } from '../components/a11y';

/*
 * Media v2 — the manager (per the media-manager mock): type chips with
 * counts, source/group/tag filters, view options (sort/columns/show-hidden/
 * auto-refresh), selection with bulk hide/show/tag, per-tile favorite/
 * hide/download on hover, new-photos refresh. Follow-ups: upload, zip
 * requests, copy-to-activation, faces filter, lightbox (with per-image
 * tag editing — the tags that scope tag-filtered slideshows).
 */

const PAGE = 60;
const AUTO_KEY = 'psv2-media-auto';

type TypeKey = 'all' | 'photos' | 'videos' | 'prints' | 'originals' | 'edited';

const TYPES: { key: TypeKey; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'photos', label: 'Photos' },
    { key: 'videos', label: 'Videos' },
    { key: 'prints', label: 'Prints' },
    { key: 'originals', label: 'Originals' },
    { key: 'edited', label: 'Edited' },
];

function matches(m: any, type: TypeKey) {
    const st = m.smileportType ?? '';
    const isVideo =
        st === 'processed-video' || (m.mimeType ?? '').startsWith('video/');
    switch (type) {
        case 'all':
            return true;
        case 'photos':
            return st === 'processed';
        case 'videos':
            return isVideo;
        case 'prints':
            return st === 'composite' || st === 'print';
        case 'originals':
            return st === 'original';
        case 'edited':
            return st === 'user-edit' || st === 'edited';
    }
}

// Mirrors cloud/modules/mediaTags/service so chips match what the server stores.
function normalizeTag(tag: string): string {
    return tag.trim().replace(/\s+/g, ' ').toLowerCase();
}

export default function MediaV2() {
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const navigate = useNavigate();
    const { getActivationPhotos } = useActivation();
    const { devices } = useActivationDevices(id);
    const authenticatedApi = useAuthenticatedApi();
    const { createZip, getZipRequestsForActivation } = useZipPhotos();
    const { createShare } = useShare();
    const { getActivations } = useActivations();
    const {
        getTagsForMedia,
        addTags,
        removeTags,
        addTagsByShareIdentifiers,
        removeTagsByShareIdentifiers,
    } = useMediaTags();
    /* gallery + domain come from the event context (IA audit: one
       resolution per activation) — the tags feature rides it too */
    const {
        galleryId,
        gallerySlug,
        domain: galleryUrl,
    } = useEventContext();
    const [searchParams, setSearchParams] = useSearchParams();

    const [media, setMedia] = useState<any[]>([]);
    const [type, setType] = useState<TypeKey>('all');
    const [source, setSource] = useState<string | null>(null);
    const [sortDesc, setSortDesc] = useState(true);
    const [cols, setCols] = useState(6);
    const [showHidden, setShowHidden] = useState(false);
    const [auto, setAuto] = useState(
        () => localStorage.getItem(AUTO_KEY) === '1',
    );
    const [limit, setLimit] = useState(PAGE);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [popover, setPopover] = useState<
        'source' | 'view' | 'group' | 'tags' | null
    >(null);
    const [newCount, setNewCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const maxId = useRef(0);
    const [zipOpen, setZipOpen] = useState(false);
    useEffect(() => {
        if (!zipOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setZipOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [zipOpen]);
    const [zipEmail, setZipEmail] = useState('');
    const [zipBusy, setZipBusy] = useState(false);
    const [zipRequests, setZipRequests] = useState<any[]>([]);
    const [copyOpen, setCopyOpen] = useState(false);
    const copyDialogRef = useDialogRef(copyOpen, () => setCopyOpen(false));
    const [copySearch, setCopySearch] = useState('');
    const [copyTargets, setCopyTargets] = useState<any[] | null>(null);
    const [copyBusy, setCopyBusy] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const uploadDialogRef = useDialogRef(uploadOpen, () =>
        setUploadOpen(false),
    );
    const [shareOpen, setShareOpen] = useState(false);
    const shareDialogRef = useDialogRef(shareOpen, () => setShareOpen(false));
    const [shareDest, setShareDest] = useState('');
    const [shareBusy, setShareBusy] = useState(false);
    const [faces, setFaces] = useState<any[]>([]);
    const [facesExpanded, setFacesExpanded] = useState(false);
    const [group, setGroup] = useState<string | null>(null);
    const [tag, setTag] = useState<string | null>(null);
    const [tagOpen, setTagOpen] = useState(false);
    const [tagMode, setTagMode] = useState<'add' | 'remove'>('add');
    const [tagPending, setTagPending] = useState<string[]>([]);
    const [tagDraft, setTagDraft] = useState('');
    const [tagBusy, setTagBusy] = useState(false);
    const [lightboxTags, setLightboxTags] = useState<MediaTag[] | null>(null);
    const [lightboxTagDraft, setLightboxTagDraft] = useState('');
    const [zipTableOpen, setZipTableOpen] = useState(false);
    const [lightbox, setLightbox] = useState<number | null>(null);
    const [replaceBusy, setReplaceBusy] = useState(false);
    const replaceRef = useRef<HTMLInputElement>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [activeBatch, setActiveBatch] = useState<EditBatch | null>(null);
    const [editHistoryOpen, setEditHistoryOpen] = useState(false);
    const [versionsMedia, setVersionsMedia] = useState<any | null>(null);

    const sendShares = async () => {
        const dest = shareDest.trim();
        if (!dest || shareBusy || galleryId == null) return;
        setShareBusy(true);
        const ok = await Promise.resolve(
            createShare({
                activationId: id,
                galleryId,
                shareIdentifiers: selectedShareIds(),
                destination: dest,
                type: dest.includes('@') ? 0 : 1,
                disclaimerAccepted: true,
            }),
        )
            .then(() => true)
            .catch(() => false);
        setShareBusy(false);
        if (!ok) {
            toast.error(`Share to ${dest} failed — try again.`);
            return;
        }
        toast(`Shared ${selected.size} to ${dest}`);
        setShareOpen(false);
        setShareDest('');
        setSelected(new Set());
    };

    useEffect(() => {
        getZipRequestsForActivation(id)
            .then((r) => setZipRequests(r?.data ?? []))
            .catch(() => {});
        authenticatedApi
            .get(`/public/media/unique-faces/${id}`)
            .then((r) => setFaces(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const selectedShareIds = () =>
        Array.from(
            new Set(
                media
                    .filter((m) => selected.has(m.id))
                    .map((m) => m.shareIdentifier)
                    .filter(Boolean),
            ),
        );

    const requestZip = async () => {
        if (!zipEmail.trim() || zipBusy) return;
        setZipBusy(true);
        const ids =
            selected.size > 0
                ? selectedShareIds()
                : Array.from(
                      new Set(
                          filtered
                              .map((m) => m.shareIdentifier)
                              .filter(Boolean),
                      ),
                  );
        await createZip(ids, zipEmail.trim(), id).catch(() => null);
        setZipBusy(false);
        setZipOpen(false);
        setZipEmail('');
        getZipRequestsForActivation(id)
            .then((r) => setZipRequests(r?.data ?? []))
            .catch(() => {});
    };

    const openCopy = () => {
        setCopyOpen(true);
        if (copyTargets == null) {
            getActivations()
                .then((rows: any[]) =>
                    setCopyTargets(
                        (rows ?? []).filter(
                            (a) => a.id !== id && !a.isArchived,
                        ),
                    ),
                )
                .catch(() => setCopyTargets([]));
        }
    };

    const copyTo = async (targetActivationId: number) => {
        if (copyBusy) return;
        setCopyBusy(true);
        const ok = await authenticatedApi
            .post('/media/copy-to-activation', {
                shareIdentifiers: selectedShareIds(),
                targetActivationId,
            })
            .then(() => true)
            .catch(() => false);
        setCopyBusy(false);
        if (!ok) {
            toast.error('Copy failed — nothing was moved. Try again.');
            return;
        }
        toast(`Copied ${selected.size} to the other activation`);
        setCopyOpen(false);
        setSelected(new Set());
    };

    const load = async (silent = false) => {
        const rows = (await getActivationPhotos(id, []).catch(() => [])) ?? [];
        setMedia(rows);
        maxId.current = rows.reduce(
            (m: number, r: any) => Math.max(m, r.id ?? 0),
            0,
        );
        setNewCount(0);
        if (!silent) setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /* poll: auto-refresh swaps silently, otherwise show the pulse */
    useEffect(() => {
        const timer = setInterval(async () => {
            const rows =
                (await getActivationPhotos(id, []).catch(() => null)) ?? null;
            if (!rows) return;
            const fresh = rows.filter((r: any) => r.id > maxId.current).length;
            if (auto && fresh > 0) {
                setMedia(rows);
                maxId.current = rows.reduce(
                    (m: number, r: any) => Math.max(m, r.id ?? 0),
                    0,
                );
            } else if (fresh > 0) {
                setNewCount(fresh);
            }
        }, 10 * 1000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, auto]);

    const deviceName = (key: string | null) => {
        if (!key) return 'Uploaded';
        const d = devices.find((x) => x.deviceKeyId === key);
        return d?.displayName ?? d?.deviceName ?? key;
    };

    const sources = useMemo(() => {
        const counts: Record<string, number> = {};
        media.forEach((m) => {
            const k = m.deviceKeyId ?? '';
            counts[k] = (counts[k] ?? 0) + 1;
        });
        return Object.entries(counts).map(([k, n]) => ({
            key: k,
            label: deviceName(k || null),
            count: n,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media, devices]);

    const filtered = useMemo(() => {
        let rows = media.filter((m) => matches(m, type));
        if (!showHidden) rows = rows.filter((m) => !m.isHidden);
        if (source != null)
            rows = rows.filter((m) => (m.deviceKeyId ?? '') === source);
        if (group != null)
            rows = rows.filter((m) => (m.groupKey ?? '') === group);
        if (tag != null)
            rows = rows.filter((m) => (m.tags ?? []).includes(tag));
        rows = [...rows].sort((a, b) => {
            const av = moment(a.capturedAt ?? a.createdAt ?? 0).valueOf();
            const bv = moment(b.capturedAt ?? b.createdAt ?? 0).valueOf();
            return sortDesc ? bv - av : av - bv;
        });
        return rows;
    }, [media, type, showHidden, source, group, tag, sortDesc]);

    const counts = useMemo(() => {
        const c: Record<string, number> = {};
        TYPES.forEach((t) => {
            c[t.key] = media.filter(
                (m) => matches(m, t.key) && (showHidden || !m.isHidden),
            ).length;
        });
        return c;
    }, [media, showHidden]);

    const visible = filtered.slice(0, limit);

    const groupKeys = useMemo(() => {
        const c: Record<string, number> = {};
        media.forEach((m) => {
            if (m.groupKey) c[m.groupKey] = (c[m.groupKey] ?? 0) + 1;
        });
        return Object.entries(c);
    }, [media]);

    /* tags present across the activation, from the rows themselves */
    const tagCounts = useMemo(() => {
        const c: Record<string, number> = {};
        media.forEach((m) =>
            (m.tags ?? []).forEach((t: string) => {
                c[t] = (c[t] ?? 0) + 1;
            }),
        );
        return Object.entries(c).sort(
            (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
        );
    }, [media]);

    /* tag suggestions for the bulk modal: activation-wide when adding,
       only tags actually on the selection when removing */
    const tagSuggestions = useMemo(() => {
        if (tagMode === 'add')
            return tagCounts
                .map(([t]) => t)
                .filter((t) => !tagPending.includes(t));
        const onSelection = new Set<string>();
        media.forEach((m) => {
            if (selected.has(m.id))
                (m.tags ?? []).forEach((t: string) => onSelection.add(t));
        });
        return Array.from(onSelection)
            .sort()
            .filter((t) => !tagPending.includes(t));
    }, [tagMode, tagCounts, tagPending, media, selected]);

    /* lightbox: ?sid= deep link opens the matching photo */
    useEffect(() => {
        const sid = searchParams.get('sid');
        if (!sid || isLoading || lightbox != null) return;
        const idx = filtered.findIndex((m) => m.shareIdentifier === sid);
        if (idx >= 0) setLightbox(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, filtered]);

    const openLightbox = (m: any) => {
        const idx = filtered.findIndex((x) => x.id === m.id);
        if (idx < 0) return;
        setLightbox(idx);
        if (m.shareIdentifier) {
            searchParams.set('sid', m.shareIdentifier);
            setSearchParams(searchParams, { replace: true });
        }
    };

    const closeLightbox = () => {
        setLightbox(null);
        searchParams.delete('sid');
        setSearchParams(searchParams, { replace: true });
    };

    const stepLightbox = (dir: -1 | 1) => {
        setLightbox((i) => {
            if (i == null) return i;
            const next = Math.min(
                filtered.length - 1,
                Math.max(0, i + dir),
            );
            const m = filtered[next];
            if (m?.shareIdentifier) {
                searchParams.set('sid', m.shareIdentifier);
                setSearchParams(searchParams, { replace: true });
            }
            return next;
        });
    };

    useEffect(() => {
        if (lightbox == null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') stepLightbox(-1);
            if (e.key === 'ArrowRight') stepLightbox(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightbox]);

    const replacePhoto = async (m: any, file: File) => {
        setReplaceBusy(true);
        const fd = new FormData();
        fd.append('photo', file);
        await authenticatedApi
            .put(`/media/replace/${m.id}`, fd)
            .catch(() => null);
        setReplaceBusy(false);
        load(true);
    };

    const toggleSelect = (m: any) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(m.id)) next.delete(m.id);
            else next.add(m.id);
            return next;
        });
    };

    const setHiddenLocal = (ids: Set<number>, isHidden: boolean) =>
        setMedia((rows) =>
            rows.map((r) => (ids.has(r.id) ? { ...r, isHidden } : r)),
        );

    const toggleTileHidden = async (m: any) => {
        setHiddenLocal(new Set([m.id]), !m.isHidden);
        await authenticatedApi
            .put(`media/${m.id}/visibility`, { isHidden: !m.isHidden })
            .catch(() => setHiddenLocal(new Set([m.id]), m.isHidden));
    };

    const toggleTileFavorite = async (m: any) => {
        setMedia((rows) =>
            rows.map((r) =>
                r.id === m.id ? { ...r, isFavorite: !m.isFavorite } : r,
            ),
        );
        const ok = await authenticatedApi
            .put(`media/${m.id}/favorite`, { isFavorite: !m.isFavorite })
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setMedia((rows) =>
                rows.map((r) =>
                    r.id === m.id
                        ? { ...r, isFavorite: m.isFavorite }
                        : r,
                ),
            );
        }
    };

    const bulkHide = async (isHidden: boolean) => {
        const rows = media.filter((m) => selected.has(m.id));
        const shareIdentifiers = Array.from(
            new Set(rows.map((m) => m.shareIdentifier).filter(Boolean)),
        );
        setHiddenLocal(selected, isHidden);
        setSelected(new Set());
        await authenticatedApi
            .put(`/media/share-identifiers/visibility`, {
                shareIdentifiers,
                isHidden,
            })
            .catch(() => load(true));
    };

    const anySelectedHidden = media.some(
        (m) => selected.has(m.id) && m.isHidden,
    );

    const setRowTags = (mediaId: number, tags: string[]) =>
        setMedia((rows) =>
            rows.map((r) => (r.id === mediaId ? { ...r, tags } : r)),
        );

    const pushPendingTag = () => {
        const t = normalizeTag(tagDraft);
        setTagDraft('');
        if (!t || tagPending.includes(t)) return;
        setTagPending((p) => [...p, t]);
    };

    const applyBulkTags = async () => {
        const tags =
            tagDraft.trim() && !tagPending.includes(normalizeTag(tagDraft))
                ? [...tagPending, normalizeTag(tagDraft)]
                : tagPending;
        if (tags.length === 0 || tagBusy) return;
        setTagBusy(true);
        const shareIdentifiers = selectedShareIds();
        await (tagMode === 'add'
            ? addTagsByShareIdentifiers(shareIdentifiers, tags)
            : removeTagsByShareIdentifiers(shareIdentifiers, tags)
        ).catch(() => null);
        setTagBusy(false);
        setTagOpen(false);
        setTagPending([]);
        setTagDraft('');
        setSelected(new Set());
        load(true);
    };

    /* lightbox tags: lazy per-photo fetch (carries provenance the grid
       rows don't have); grid rows are kept in sync on every write */
    const lightboxMediaId =
        lightbox != null ? filtered[lightbox]?.id ?? null : null;
    useEffect(() => {
        if (lightboxMediaId == null) {
            setLightboxTags(null);
            return;
        }
        let mounted = true;
        setLightboxTags(null);
        setLightboxTagDraft('');
        getTagsForMedia(lightboxMediaId)
            .then((r) => {
                if (mounted) setLightboxTags(r.data);
            })
            .catch(() => null);
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightboxMediaId]);

    const addLightboxTag = async (m: any) => {
        const t = normalizeTag(lightboxTagDraft);
        if (!t) return;
        setLightboxTagDraft('');
        const res = await addTags(m.id, [t]).catch(() => null);
        if (res == null) return;
        setLightboxTags(res.data);
        setRowTags(
            m.id,
            res.data.map((x) => x.tag),
        );
    };

    const removeLightboxTag = async (m: any, t: string) => {
        const before = lightboxTags ?? [];
        const after = before.filter((x) => x.tag !== t);
        setLightboxTags(after);
        setRowTags(
            m.id,
            after.map((x) => x.tag),
        );
        const res = await removeTags(m.id, [t]).catch(() => null);
        if (res == null) {
            setLightboxTags(before);
            setRowTags(
                m.id,
                before.map((x) => x.tag),
            );
        }
    };

    const pill =
        'ps-pill flex cursor-pointer items-center gap-1.5 px-3.5 font-[inherit] text-[12.5px] font-semibold text-foreground';

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="grid grid-cols-6 gap-2">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div
                            key={i}
                            className="aspect-square animate-pulse rounded-[10px] bg-secondary"
                        />
                    ))}
                </div>
            </main>
        );
    }

    return (
        <main
            className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4"
            onClick={() => {
                if (popover) setPopover(null);
                if (zipOpen) setZipOpen(false);
            }}
        >
            {/* row 1: types + actions */}
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <span className="ps-label mr-2.5">
                    Media
                    <button
                        type="button"
                        onClick={() =>
                            navigate(`/v2/activations/${id}/analyze`)
                        }
                        className="ps-label ml-2.5 cursor-pointer border-0 bg-transparent p-0 text-faint hover:text-foreground"
                    >
                        Analyze →
                    </button>
                </span>
                {TYPES.filter(
                    (t) => t.key === 'all' || counts[t.key] > 0,
                ).map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                            setType(t.key);
                            setLimit(PAGE);
                        }}
                        className={
                            'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                            (type === t.key
                                ? 'border border-transparent bg-brand-soft text-brand-ink'
                                : 'border border-border bg-transparent text-muted-foreground')
                        }
                    >
                        {t.label}
                        <span className="ml-1.5 font-normal tabular-nums text-faint">
                            {counts[t.key].toLocaleString()}
                        </span>
                    </button>
                ))}
                <div className="ml-auto flex gap-1.5">
                    <button
                        type="button"
                        className={pill}
                        onClick={() => setUploadOpen(true)}
                    >
                        Upload
                    </button>
                    <button
                        type="button"
                        title="Bulk edit history"
                        className={pill}
                        onClick={() => setEditHistoryOpen(true)}
                    >
                        Edits
                    </button>
                    <div className="relative">
                        <button
                            type="button"
                            className={pill}
                            onClick={(e) => {
                                e.stopPropagation();
                                setZipOpen((o) => !o);
                                setPopover(null);
                            }}
                        >
                            Download
                        </button>
                        {zipOpen && (
                            <div
                                className="absolute right-0 top-10 z-40 w-[260px] rounded-[13px] border border-border bg-background p-3 shadow-lg"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="ps-label mb-1.5">
                                    Zip {selected.size > 0
                                        ? `${selected.size} selected`
                                        : `all ${filtered.length}`}
                                </div>
                                <input
                                    className="ps-input mb-2 w-full text-[12.5px]"
                                    placeholder="Email for the zip link"
                                    value={zipEmail}
                                    onChange={(e) =>
                                        setZipEmail(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') requestZip();
                                    }}
                                />
                                <button
                                    type="button"
                                    disabled={zipBusy || !zipEmail.trim()}
                                    onClick={requestZip}
                                    className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                                >
                                    {zipBusy ? 'Requesting…' : 'Create & send zip'}
                                </button>
                                <p className="mt-2 text-[10.5px] text-faint">
                                    Large exports split at 5,000 photos.
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="relative">
                        <button
                            type="button"
                            title="Filter by source"
                            className={pill + ' w-[34px] justify-center px-0'}
                            onClick={(e) => {
                                e.stopPropagation();
                                setPopover((p) =>
                                    p === 'source' ? null : 'source',
                                );
                            }}
                        >
                            <svg
                                viewBox="0 0 20 20"
                                className="h-3.5 w-3.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinecap="round"
                            >
                                <path d="M3 5h14M6 10h8M8.5 15h3" />
                            </svg>
                        </button>
                        {popover === 'source' && (
                            <div
                                className="absolute right-0 top-10 z-40 w-[240px] rounded-[13px] border border-border bg-background p-2 shadow-lg"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {[{ key: null as any, label: 'All sources', count: media.length }, ...sources].map(
                                    (s2) => (
                                        <button
                                            key={s2.key ?? '__all'}
                                            type="button"
                                            onClick={() => {
                                                setSource(s2.key);
                                                setLimit(PAGE);
                                                setPopover(null);
                                            }}
                                            className={
                                                'flex w-full cursor-pointer items-baseline gap-2 rounded-[8px] border-0 bg-transparent px-2.5 py-1.5 text-left font-[inherit] text-[12.5px] hover:bg-secondary ' +
                                                (source === s2.key
                                                    ? 'font-semibold text-brand-ink'
                                                    : 'text-foreground')
                                            }
                                        >
                                            <span className="truncate">
                                                {s2.label}
                                            </span>
                                            <span className="ml-auto tabular-nums text-faint">
                                                {s2.count}
                                            </span>
                                        </button>
                                    ),
                                )}
                            </div>
                        )}
                    </div>
                    {groupKeys.length > 0 && (
                        <div className="relative">
                            <button
                                type="button"
                                title="Filter by group"
                                className={pill + ' w-[34px] justify-center px-0'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPopover((p2) =>
                                        p2 === 'group' ? null : 'group',
                                    );
                                }}
                            >
                                ⌗
                            </button>
                            {popover === 'group' && (
                                <div
                                    className="absolute right-0 top-10 z-40 w-[220px] rounded-[13px] border border-border bg-background p-2 shadow-lg"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {[['__all', media.length] as any, ...groupKeys].map(
                                        ([k, n]: any) => (
                                            <button
                                                key={k}
                                                type="button"
                                                onClick={() => {
                                                    setGroup(
                                                        k === '__all'
                                                            ? null
                                                            : k,
                                                    );
                                                    setLimit(PAGE);
                                                    setPopover(null);
                                                }}
                                                className={
                                                    'flex w-full cursor-pointer items-baseline gap-2 rounded-[8px] border-0 bg-transparent px-2.5 py-1.5 text-left font-[inherit] text-[12.5px] hover:bg-secondary ' +
                                                    ((k === '__all' &&
                                                        group == null) ||
                                                    group === k
                                                        ? 'font-semibold text-brand-ink'
                                                        : 'text-foreground')
                                                }
                                            >
                                                <span className="truncate">
                                                    {k === '__all'
                                                        ? 'All groups'
                                                        : k}
                                                </span>
                                                <span className="ml-auto tabular-nums text-faint">
                                                    {n}
                                                </span>
                                            </button>
                                        ),
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {tagCounts.length > 0 && (
                        <div className="relative">
                            <button
                                type="button"
                                title="Filter by tag"
                                className={pill + ' w-[34px] justify-center px-0'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPopover((p2) =>
                                        p2 === 'tags' ? null : 'tags',
                                    );
                                }}
                            >
                                <svg
                                    viewBox="0 0 20 20"
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.7"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M3.5 3.5h5.6L17 11.4a1.6 1.6 0 0 1 0 2.3l-3.3 3.3a1.6 1.6 0 0 1-2.3 0L3.5 9.1z" />
                                    <circle cx="7" cy="7" r="0.5" />
                                </svg>
                            </button>
                            {popover === 'tags' && (
                                <div
                                    className="absolute right-0 top-10 z-40 max-h-[320px] w-[240px] overflow-y-auto rounded-[13px] border border-border bg-background p-2 shadow-lg"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {[
                                        ['__all', media.length] as [
                                            string,
                                            number,
                                        ],
                                        ...tagCounts,
                                    ].map(([t, n]) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => {
                                                setTag(
                                                    t === '__all' ? null : t,
                                                );
                                                setLimit(PAGE);
                                                setPopover(null);
                                            }}
                                            className={
                                                'flex w-full cursor-pointer items-baseline gap-2 rounded-[8px] border-0 bg-transparent px-2.5 py-1.5 text-left font-[inherit] text-[12.5px] hover:bg-secondary ' +
                                                ((t === '__all' &&
                                                    tag == null) ||
                                                tag === t
                                                    ? 'font-semibold text-brand-ink'
                                                    : 'text-foreground')
                                            }
                                        >
                                            <span className="truncate">
                                                {t === '__all'
                                                    ? 'All tags'
                                                    : t}
                                            </span>
                                            <span className="ml-auto tabular-nums text-faint">
                                                {n}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="relative">
                        <button
                            type="button"
                            title="View options"
                            className={pill + ' w-[34px] justify-center px-0'}
                            onClick={(e) => {
                                e.stopPropagation();
                                setPopover((p) =>
                                    p === 'view' ? null : 'view',
                                );
                            }}
                        >
                            ⚙
                        </button>
                        {popover === 'view' && (
                            <div
                                className="absolute right-0 top-10 z-40 w-[220px] rounded-[13px] border border-border bg-background p-3 shadow-lg"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="ps-label mb-1.5">Sort</div>
                                <div className="mb-3 inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                                    {[
                                        { l: 'Newest', v: true },
                                        { l: 'Oldest', v: false },
                                    ].map((o) => (
                                        <button
                                            key={o.l}
                                            type="button"
                                            onClick={() => setSortDesc(o.v)}
                                            className={
                                                'cursor-pointer rounded-[7px] border-0 px-3 py-1 font-[inherit] text-[12px] font-semibold ' +
                                                (sortDesc === o.v
                                                    ? 'bg-background text-foreground'
                                                    : 'bg-transparent text-muted-foreground')
                                            }
                                        >
                                            {o.l}
                                        </button>
                                    ))}
                                </div>
                                <div className="ps-label mb-1.5">Columns</div>
                                <div className="mb-3 inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                                    {[3, 4, 5, 6].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            onClick={() => setCols(n)}
                                            className={
                                                'cursor-pointer rounded-[7px] border-0 px-2.5 py-1 font-[inherit] text-[12px] font-semibold tabular-nums ' +
                                                (cols === n
                                                    ? 'bg-background text-foreground'
                                                    : 'bg-transparent text-muted-foreground')
                                            }
                                        >
                                            {n}
                                        </button>
                                    ))}
                                </div>
                                {[
                                    {
                                        l: 'Show hidden',
                                        v: showHidden,
                                        set: () =>
                                            setShowHidden((x) => !x),
                                    },
                                    {
                                        l: 'Auto refresh',
                                        v: auto,
                                        set: () => {
                                            setAuto((x) => {
                                                localStorage.setItem(
                                                    AUTO_KEY,
                                                    x ? '0' : '1',
                                                );
                                                return !x;
                                            });
                                        },
                                    },
                                ].map((o) => (
                                    <button
                                        key={o.l}
                                        type="button"
                                        onClick={o.set}
                                        className="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent px-0 py-1.5 font-[inherit] text-[12.5px] text-foreground"
                                    >
                                        {o.l}
                                        <span
                                            className={
                                                'text-[11px] font-semibold ' +
                                                (o.v
                                                    ? 'text-brand-ink'
                                                    : 'text-faint')
                                            }
                                        >
                                            {o.v ? 'On' : 'Off'}
                                        </span>
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={async () => {
                                        setPopover(null);
                                        await authenticatedApi
                                            .post(
                                                `/activations/${id}/reprocessThumbnails`,
                                            )
                                            .catch(() => null);
                                    }}
                                    className="mt-1 w-full cursor-pointer border-0 border-t border-line bg-transparent px-0 pt-2 text-left font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                                >
                                    Reprocess thumbnails
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* row 2: active filter chips + new-photos pulse */}
            {(source != null || group != null || tag != null || newCount > 0 || zipRequests.length > 0) && (
                <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
                    {source != null && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border py-[5px] pl-3 pr-1.5 text-[12px] font-semibold text-muted-foreground">
                            source is{' '}
                            <b className="text-foreground">
                                {deviceName(source || null)}
                            </b>
                            <button
                                type="button"
                                onClick={() => setSource(null)}
                                className="cursor-pointer border-0 bg-transparent px-1 text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    )}
                    {group != null && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border py-[5px] pl-3 pr-1.5 text-[12px] font-semibold text-muted-foreground">
                            group is{' '}
                            <b className="text-foreground">{group}</b>
                            <button
                                type="button"
                                onClick={() => setGroup(null)}
                                className="cursor-pointer border-0 bg-transparent px-1 text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    )}
                    {tag != null && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border py-[5px] pl-3 pr-1.5 text-[12px] font-semibold text-muted-foreground">
                            tag is{' '}
                            <b className="text-foreground">{tag}</b>
                            <button
                                type="button"
                                onClick={() => setTag(null)}
                                className="cursor-pointer border-0 bg-transparent px-1 text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    )}
                    {zipRequests.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setZipTableOpen((o) => !o)}
                            className="cursor-pointer border-0 bg-transparent p-0 text-left font-[inherit] text-[11.5px] text-faint hover:text-foreground">
                            {(() => {
                                const done = zipRequests.filter(
                                    (r: any) => r.zipRequestUri,
                                );
                                const pending =
                                    zipRequests.length - done.length;
                                return (
                                    <>
                                        {pending > 0 &&
                                            `${pending} zip export${
                                                pending === 1 ? '' : 's'
                                            } in progress`}
                                        {pending > 0 && done.length > 0 &&
                                            ' · '}
                                        {done.length > 0 && (
                                            <a
                                                href={
                                                    done[0].zipRequestUri
                                                }
                                                className="font-semibold text-brand-ink no-underline hover:underline"
                                            >
                                                {done.length} ready →
                                            </a>
                                        )}
                                    </>
                                );
                            })()}
                            {' '}
                            {zipTableOpen ? '▴' : '▾'}
                        </button>
                    )}
                    {newCount > 0 && (
                        <button
                            type="button"
                            onClick={() => load(true)}
                            className="ml-auto flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                        >
                            <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-brand" />
                            {newCount} new — refresh
                        </button>
                    )}
                </div>
            )}

            {faces.length > 0 && (
                <div className="mb-3 flex items-center gap-2">
                    <span className="ps-label mr-1">People</span>
                    {(facesExpanded ? faces : faces.slice(0, 10)).map(
                        (f: any) => (
                            <button
                                key={f.faceVector}
                                type="button"
                                title="Open this person's photos"
                                onClick={() => {
                                    if (!gallerySlug) return;
                                    window.open(
                                        `${galleryUrl}/face-query/${encodeURIComponent(
                                            gallerySlug,
                                        )}?faceIds=${encodeURIComponent(
                                            f.faceVector,
                                        )}`,
                                        '_blank',
                                    );
                                }}
                                className="-ml-1 h-[34px] w-[34px] cursor-pointer overflow-hidden rounded-full border-2 border-background bg-secondary p-0 first:ml-0"
                            >
                                <img
                                    src={f.faceCatalogUrl}
                                    alt=""
                                    loading="lazy"
                                    className="h-full w-full object-cover"
                                />
                            </button>
                        ),
                    )}
                    {faces.length > 10 && !facesExpanded && (
                        <button
                            type="button"
                            onClick={() => setFacesExpanded(true)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-foreground"
                        >
                            +{faces.length - 10} more
                        </button>
                    )}
                </div>
            )}

            {zipTableOpen && zipRequests.length > 0 && (
                <div className="mb-3 rounded-[12px] border border-border p-3">
                    {zipRequests.map((r: any, i: number) => (
                        <div
                            key={r.id ?? i}
                            className={
                                'flex items-baseline gap-3 py-1.5 text-[11.5px] ' +
                                (i > 0 ? 'border-t border-line' : '')
                            }
                        >
                            <span className="min-w-0 flex-1 truncate text-muted-foreground">
                                {r.zipRequestDestination ?? '—'}
                            </span>
                            <span
                                className={
                                    'font-semibold ' +
                                    (r.zipRequestUri
                                        ? 'text-good'
                                        : 'text-faint')
                                }
                            >
                                {r.zipRequestUri
                                    ? 'ready'
                                    : r.status ?? 'processing'}
                            </span>
                            <span className="whitespace-nowrap text-faint">
                                {r.createdAt
                                    ? moment(r.createdAt).fromNow(true)
                                    : ''}
                            </span>
                            {r.zipRequestUri && (
                                <a
                                    href={r.zipRequestUri}
                                    className="font-semibold text-brand-ink no-underline hover:underline"
                                >
                                    Download →
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {filtered.length === 0 ? (
                <p className="py-6 text-[13px] text-muted-foreground">
                    No media {source != null || type !== 'all' ? 'matches' : 'yet — it lands here as devices capture'}
                    .
                </p>
            ) : (
                <>
                    <div
                        className="grid gap-2"
                        style={{
                            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                        }}
                    >
                        {visible.map((m) => {
                            const isVideo =
                                m.smileportType === 'processed-video' ||
                                (m.mimeType ?? '').startsWith('video/');
                            const isSel = selected.has(m.id);
                            return (
                                <div
                                    key={m.id}
                                    role="checkbox"
                                    aria-checked={isSel}
                                    aria-label="Select photo"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (
                                            e.target !== e.currentTarget
                                        )
                                            return;
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            e.preventDefault();
                                            toggleSelect(m);
                                        }
                                    }}
                                    onClick={() => toggleSelect(m)}
                                    className={
                                        'group relative aspect-square cursor-pointer overflow-hidden rounded-[10px] bg-secondary ' +
                                        (isSel
                                            ? 'ring-[2.5px] ring-inset ring-brand'
                                            : '')
                                    }
                                >
                                    <img
                                        src={
                                            m.thumbnailUri ??
                                            m.thumbnailTinyUri ??
                                            m.originalUri
                                        }
                                        alt=""
                                        loading="lazy"
                                        className={
                                            'h-full w-full object-cover ' +
                                            (m.isHidden ? 'opacity-40' : '')
                                        }
                                    />
                                    {isSel && (
                                        <span className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-[11px] text-brand-foreground">
                                            ✓
                                        </span>
                                    )}
                                    {!isSel && m.isFavorite && (
                                        <span className="absolute left-1.5 top-1.5 text-[13px] text-white drop-shadow">
                                            ♥
                                        </span>
                                    )}
                                    {m.isHidden && !isSel && (
                                        <span className="absolute left-1.5 top-1.5 rounded-[5px] bg-secondary px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground">
                                            Hidden
                                        </span>
                                    )}
                                    {isVideo && (
                                        <span className="absolute bottom-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-[9px] text-white">
                                            ▶
                                        </span>
                                    )}
                                    <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                        {[
                                            {
                                                t: m.isFavorite
                                                    ? 'Unfavorite'
                                                    : 'Favorite',
                                                icon: '♥',
                                                on: () =>
                                                    toggleTileFavorite(m),
                                            },
                                            {
                                                t: m.isHidden
                                                    ? 'Show'
                                                    : 'Hide',
                                                icon: m.isHidden ? '◎' : '⊘',
                                                on: () =>
                                                    toggleTileHidden(m),
                                            },
                                            {
                                                t: 'View',
                                                icon: '⤢',
                                                on: () => openLightbox(m),
                                            },
                                        ].map((a) => (
                                            <button
                                                key={a.t}
                                                type="button"
                                                title={a.t}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    a.on();
                                                }}
                                                className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[7px] border-0 bg-black/45 text-[11px] text-white"
                                            >
                                                {a.icon}
                                            </button>
                                        ))}
                                    </div>
                                    {(m.tags ?? []).length > 0 && (
                                        <div className="absolute bottom-1.5 left-1.5 flex max-w-[85%] flex-wrap gap-1 transition-opacity group-hover:opacity-0">
                                            {m.tags
                                                .slice(0, 2)
                                                .map((t: string) => (
                                                    <span
                                                        key={t}
                                                        className="max-w-[110px] truncate rounded-full bg-black/45 px-1.5 py-0.5 text-[9px] text-white"
                                                    >
                                                        {t}
                                                    </span>
                                                ))}
                                            {m.tags.length > 2 && (
                                                <span className="rounded-full bg-black/45 px-1.5 py-0.5 text-[9px] text-white">
                                                    +{m.tags.length - 2}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <span className="absolute bottom-1.5 left-1.5 rounded-[5px] bg-black/45 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.06em] text-white opacity-0 transition-opacity group-hover:opacity-100">
                                        {deviceName(m.deviceKeyId ?? null)}
                                        {(m.capturedAt ?? m.createdAt) &&
                                            ` · ${moment(
                                                m.capturedAt ?? m.createdAt,
                                            ).format('h:mm A')}`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex items-center justify-between py-4">
                        <span className="text-[12px] text-faint">
                            {visible.length.toLocaleString()} of{' '}
                            {filtered.length.toLocaleString()} ·{' '}
                            {sortDesc ? 'newest' : 'oldest'} first
                        </span>
                        {filtered.length > limit && (
                            <button
                                type="button"
                                onClick={() => setLimit((l) => l + PAGE)}
                                className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                            >
                                Show more
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* selection bar */}
            {selected.size > 0 && (
                <div className="fixed bottom-[88px] left-1/2 z-30 flex -translate-x-1/2 items-center gap-3.5 rounded-full border border-border bg-background px-[18px] py-[9px] text-[12.5px] shadow-[0_8px_28px_-6px_rgba(20,23,29,0.25)]">
                    <b className="font-semibold">
                        {selected.size} selected
                    </b>
                    <button
                        type="button"
                        onClick={() => bulkHide(!anySelectedHidden)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        {anySelectedHidden ? 'Show' : 'Hide'}
                    </button>
                    <button
                        type="button"
                        onClick={async () => {
                            /* one file opens directly; more goes through
                               the zip service (N window.opens get eaten
                               by popup blockers) */
                            const sel = media.filter((m) =>
                                selected.has(m.id),
                            );
                            if (sel.length === 1) {
                                window.open(sel[0].originalUri, '_blank');
                                return;
                            }
                            const email = window.prompt(
                                `Zip ${sel.length} originals — email the download link to:`,
                            );
                            if (!email?.trim()) return;
                            const ok = await createZip(
                                selectedShareIds(),
                                email.trim(),
                                id,
                            )
                                .then(() => true)
                                .catch(() => false);
                            if (ok) {
                                toast(
                                    `Zip started — the link lands at ${email.trim()} and under Download requests.`,
                                );
                                getZipRequestsForActivation(id)
                                    .then((r) =>
                                        setZipRequests(r?.data ?? []),
                                    )
                                    .catch(() => {});
                            } else {
                                toast.error(
                                    'Zip request failed — try again.',
                                );
                            }
                        }}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        Download
                    </button>
                    <button
                        type="button"
                        onClick={() => setEditOpen(true)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        Edit…
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setTagMode('add');
                            setTagPending([]);
                            setTagDraft('');
                            setTagOpen(true);
                        }}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        Tag…
                    </button>
                    <button
                        type="button"
                        onClick={() => setShareOpen(true)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        Share…
                    </button>
                    <button
                        type="button"
                        onClick={openCopy}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        Copy to…
                    </button>
                    <button
                        type="button"
                        onClick={() => setSelected(new Set())}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-faint"
                    >
                        Clear
                    </button>
                </div>
            )}

            {/* lightbox */}
            {lightbox != null && filtered[lightbox] && (
                <div
                    {...dialogPanelProps('Photo viewer')}
                    className="fixed inset-0 z-50 flex flex-col bg-black/90 outline-none"
                    onClick={closeLightbox}
                >
                    <div className="flex items-center justify-between px-5 py-3 text-white/80">
                        <span className="font-mono text-[11px] tabular-nums">
                            {lightbox + 1} / {filtered.length}
                        </span>
                        <button
                            type="button"
                            onClick={closeLightbox}
                            className="cursor-pointer border-0 bg-transparent p-0 text-[18px] text-white/80 hover:text-white"
                        >
                            ×
                        </button>
                    </div>
                    <div
                        className="relative flex min-h-0 flex-1 items-center justify-center px-14"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {lightbox > 0 && (
                            <button
                                type="button"
                                onClick={() => stepLightbox(-1)}
                                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-white/10 text-[18px] text-white hover:bg-white/20"
                            >
                                ‹
                            </button>
                        )}
                        {(() => {
                            const m = filtered[lightbox];
                            const isVideo =
                                m.smileportType === 'processed-video' ||
                                (m.mimeType ?? '').startsWith('video/');
                            return isVideo ? (
                                <video
                                    key={m.id}
                                    src={m.originalUri}
                                    controls
                                    className="max-h-full max-w-full rounded-[10px]"
                                />
                            ) : (
                                <img
                                    key={m.id}
                                    src={
                                        m.thumbnail_800pxUri ??
                                        m.originalUri ??
                                        m.thumbnailUri
                                    }
                                    alt=""
                                    className={
                                        'max-h-full max-w-full rounded-[10px] object-contain ' +
                                        (m.isHidden ? 'opacity-50' : '')
                                    }
                                />
                            );
                        })()}
                        {lightbox < filtered.length - 1 && (
                            <button
                                type="button"
                                onClick={() => stepLightbox(1)}
                                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-white/10 text-[18px] text-white hover:bg-white/20"
                            >
                                ›
                            </button>
                        )}
                    </div>
                    {/* tags: chips + inline add; booth-applied vs operator provenance */}
                    <div
                        className="flex flex-wrap items-center justify-center gap-1.5 px-5 pt-2.5 text-[11.5px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(lightboxTags ?? []).map((t) => {
                            const booth =
                                t.source === 'capture' ||
                                t.source === 'action';
                            return (
                                <span
                                    key={t.tag}
                                    title={
                                        booth
                                            ? `Applied by the booth (${t.source})`
                                            : 'Added by an operator'
                                    }
                                    className={
                                        'inline-flex items-center gap-1 rounded-full py-[3px] pl-2.5 pr-1 font-semibold ' +
                                        (booth
                                            ? 'bg-brand/25 text-white/90'
                                            : 'bg-white/10 text-white/85')
                                    }
                                >
                                    {t.tag}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            removeLightboxTag(
                                                filtered[lightbox],
                                                t.tag,
                                            )
                                        }
                                        className="cursor-pointer border-0 bg-transparent px-1 text-white/50 hover:text-white"
                                    >
                                        ×
                                    </button>
                                </span>
                            );
                        })}
                        <input
                            value={lightboxTagDraft}
                            onChange={(e) =>
                                setLightboxTagDraft(e.target.value)
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                    addLightboxTag(filtered[lightbox]);
                                e.stopPropagation();
                            }}
                            placeholder={
                                lightboxTags == null
                                    ? 'Loading tags…'
                                    : '+ add tag'
                            }
                            disabled={lightboxTags == null}
                            className="w-[110px] rounded-full border border-white/20 bg-transparent px-2.5 py-[3px] font-[inherit] text-[11.5px] text-white/85 placeholder:text-white/40 focus:border-white/50 focus:outline-none"
                        />
                    </div>
                    <div
                        className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 px-5 py-3.5 text-[12px] text-white/70"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const m = filtered[lightbox];
                            return (
                                <>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.06em]">
                                        {deviceName(m.deviceKeyId ?? null)}
                                        {(m.capturedAt ?? m.createdAt) &&
                                            ` · ${moment(
                                                m.capturedAt ?? m.createdAt,
                                            ).format('MMM D, h:mm A')}`}
                                        {m.isHidden && ' · HIDDEN'}
                                    </span>
                                    {[
                                        {
                                            t: m.isFavorite
                                                ? '♥ Unfavorite'
                                                : '♡ Favorite',
                                            on: () => toggleTileFavorite(m),
                                        },
                                        {
                                            t: m.isHidden ? 'Show' : 'Hide',
                                            on: () => toggleTileHidden(m),
                                        },
                                        {
                                            t: replaceBusy
                                                ? 'Replacing…'
                                                : 'Replace',
                                            on: () =>
                                                replaceRef.current?.click(),
                                        },
                                        {
                                            t: 'History',
                                            on: () => setVersionsMedia(m),
                                        },
                                        {
                                            t: 'Open original',
                                            on: () =>
                                                window.open(
                                                    m.originalUri,
                                                    '_blank',
                                                ),
                                        },
                                    ].map((a) => (
                                        <button
                                            key={a.t}
                                            type="button"
                                            onClick={a.on}
                                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-white/85 hover:text-white"
                                        >
                                            {a.t}
                                        </button>
                                    ))}
                                    <input
                                        ref={replaceRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            e.target.value = '';
                                            if (f) replacePhoto(m, f);
                                        }}
                                    />
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* bulk tag: add/remove tags across the selection */}
            {tagOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setTagOpen(false)}
                >
                    <div
                        className="w-full max-w-[380px] rounded-[15px] border border-border bg-background p-5 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-2.5">
                            Tag {selected.size} photo
                            {selected.size === 1 ? '' : 's'}
                        </div>
                        <div className="mb-3 inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                            {[
                                { l: 'Add tags', v: 'add' as const },
                                { l: 'Remove tags', v: 'remove' as const },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    type="button"
                                    onClick={() => {
                                        setTagMode(o.v);
                                        setTagPending([]);
                                    }}
                                    className={
                                        'cursor-pointer rounded-[7px] border-0 px-3 py-1 font-[inherit] text-[12px] font-semibold ' +
                                        (tagMode === o.v
                                            ? 'bg-background text-foreground'
                                            : 'bg-transparent text-muted-foreground')
                                    }
                                >
                                    {o.l}
                                </button>
                            ))}
                        </div>
                        <div className="mb-2 flex flex-wrap items-center gap-1.5">
                            {tagPending.map((t) => (
                                <span
                                    key={t}
                                    className="inline-flex items-center gap-1 rounded-full bg-brand-soft py-[3px] pl-2.5 pr-1 text-[12px] font-semibold text-brand-ink"
                                >
                                    {t}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setTagPending((p) =>
                                                p.filter((x) => x !== t),
                                            )
                                        }
                                        className="cursor-pointer border-0 bg-transparent px-1 text-faint hover:text-attention"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            <input
                                autoFocus
                                className="ps-input w-[140px] text-[12.5px]"
                                placeholder="Type a tag, Enter"
                                value={tagDraft}
                                onChange={(e) => setTagDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        if (
                                            tagDraft.trim() === '' &&
                                            tagPending.length > 0
                                        )
                                            applyBulkTags();
                                        else pushPendingTag();
                                    }
                                    if (e.key === 'Escape')
                                        setTagOpen(false);
                                }}
                            />
                        </div>
                        {tagSuggestions.length > 0 && (
                            <div className="mb-3 flex flex-wrap items-center gap-1">
                                {tagSuggestions.slice(0, 12).map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() =>
                                            setTagPending((p) => [...p, t])
                                        }
                                        className="cursor-pointer rounded-full border border-dashed border-border bg-transparent px-2.5 py-[3px] font-[inherit] text-[11.5px] text-muted-foreground hover:text-brand-ink"
                                    >
                                        + {t}
                                    </button>
                                ))}
                            </div>
                        )}
                        <button
                            type="button"
                            disabled={
                                tagBusy ||
                                (tagPending.length === 0 && !tagDraft.trim())
                            }
                            onClick={applyBulkTags}
                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white disabled:opacity-50"
                        >
                            {tagBusy
                                ? 'Applying…'
                                : tagMode === 'add'
                                ? 'Add tags'
                                : 'Remove tags'}
                        </button>
                        {tagMode === 'remove' && (
                            <p className="mt-2 text-[10.5px] text-faint">
                                Removes the tags above from every selected
                                photo that has them.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* share prompt */}
            {shareOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setShareOpen(false)}
                >
                    <div
                        ref={shareDialogRef}
                        {...dialogPanelProps('Share selected media')}
                        className="w-full max-w-[380px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-2.5">
                            Share {selected.size} to…
                        </div>
                        <input
                            autoFocus
                            className="ps-input mb-3 w-full text-[13px]"
                            placeholder="Email or phone number"
                            value={shareDest}
                            onChange={(e) => setShareDest(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') sendShares();
                                if (e.key === 'Escape') setShareOpen(false);
                            }}
                        />
                        <button
                            type="button"
                            disabled={shareBusy || !shareDest.trim()}
                            onClick={sendShares}
                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                        >
                            {shareBusy ? 'Sending…' : 'Send shares'}
                        </button>
                    </div>
                </div>
            )}

            {/* copy-to picker */}
            {copyOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setCopyOpen(false)}
                >
                    <div
                        ref={copyDialogRef}
                        {...dialogPanelProps('Copy to another activation')}
                        className="flex max-h-[60vh] w-full max-w-[420px] flex-col rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-2.5">
                            Copy {selected.size} to…
                        </div>
                        <SearchField
                            className="mb-1.5 w-full"
                            placeholder="Search activations…"
                            value={copySearch}
                            onChange={setCopySearch}
                        />
                        <div className="flex-1 overflow-y-auto">
                            {copyTargets == null && (
                                <p className="py-2 text-[12.5px] text-faint">
                                    Loading…
                                </p>
                            )}
                            {(copyTargets ?? [])
                                .filter((a) =>
                                    `${a.name}`
                                        .toLowerCase()
                                        .includes(
                                            copySearch.toLowerCase(),
                                        ),
                                )
                                .slice(0, 30)
                                .map((a) => (
                                    <button
                                        key={a.id}
                                        type="button"
                                        disabled={copyBusy}
                                        onClick={() => copyTo(a.id)}
                                        className="flex w-full cursor-pointer items-baseline gap-2.5 border-0 bg-transparent px-0.5 py-1.5 text-left font-[inherit] text-[12.5px] font-semibold hover:text-brand-ink"
                                    >
                                        {a.name}
                                        <span className="ml-auto font-normal text-[11px] text-faint">
                                            {eventDay(a.date).format(
                                                'MMM D, YYYY',
                                            )}
                                        </span>
                                    </button>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            {/* upload — classic uploader, v2 overlay */}
            {uploadOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setUploadOpen(false)}
                >
                    <div
                        ref={uploadDialogRef}
                        {...dialogPanelProps('Upload media')}
                        className="max-h-[80vh] w-full max-w-[560px] overflow-y-auto rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <BulkUploadMedia
                            activationId={id}
                            galleryId={galleryId}
                            onCancel={() => setUploadOpen(false)}
                            onComplete={() => {
                                setUploadOpen(false);
                                load(true);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* bulk edit: crop config → progress/report → history/rollback */}
            {editOpen && (
                <BulkEditModal
                    activationId={id}
                    galleryId={galleryId}
                    media={media.filter((m) => selected.has(m.id))}
                    onClose={() => setEditOpen(false)}
                    onStarted={(batch) => {
                        setEditOpen(false);
                        setSelected(new Set());
                        setActiveBatch(batch);
                    }}
                />
            )}
            {activeBatch && (
                <EditBatchProgress
                    batch={activeBatch}
                    onClose={() => {
                        setActiveBatch(null);
                        load(true);
                    }}
                    onChanged={() => load(true)}
                />
            )}
            {editHistoryOpen && (
                <EditHistoryModal
                    activationId={id}
                    onClose={() => setEditHistoryOpen(false)}
                    onChanged={() => load(true)}
                />
            )}
            {versionsMedia && (
                <VersionHistoryModal
                    media={versionsMedia}
                    onClose={() => setVersionsMedia(null)}
                    onChanged={() => load(true)}
                />
            )}
        </main>
    );
}
